import { mediaUrl } from '../../../lib/media-url.js';
import { isStripeCheckout } from '../../../lib/checkout-mode.js';
import { useState } from 'react';
import { ShoppingCart, Check, Send, Package, Cpu } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../../components/ui/Card.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { cn, formatCurrency } from '../../../lib/utils.js';
import { useCart } from '../../../store/cart.jsx';

export function SpecsHeavyProductCard({
  product,
  tenant,
  onAddToCart,
  onViewProduct,
  onInquiry,
  compact = false,
  index = 0,
  style,
  className,
  ...props
}) {
  let isInCartFn = () => false;
  let addItemFn = () => {};
  try {
    const cart = useCart();
    if (cart) {
      isInCartFn = cart.isInCart;
      addItemFn = cart.addItem;
    }
  } catch {}

  const sku = product?.sku || product?.id || '';
  const alreadyInCart = sku ? isInCartFn(sku) : false;

  let images = [];
  try {
    images = Array.isArray(product?.images)
      ? product.images
      : (product?.images_json ? JSON.parse(product.images_json) : []);
  } catch {
    images = [];
  }
  const primaryImage = (images && images[0]?.url) || (typeof images[0] === 'string' && images[0]) || product?.primaryImageUrl || product?.image_url || '';

  const unitPriceCents = product?.price?.cents !== undefined
    ? Number(product.price.cents)
    : (product?.base_price_cents !== undefined
      ? Number(product.base_price_cents)
      : (product?.base_price !== undefined ? Number(product.base_price) : (Number(product?.unitPriceCents) || 0)));

  const isBuyable = unitPriceCents > 0;
  const formattedPrice = product?.price?.formatted || formatCurrency(unitPriceCents);

  let specs = {};
  try {
    specs = product?.specs_json ? JSON.parse(product.specs_json) : (product?.specs || {});
  } catch {
    specs = {};
  }

  const handleCardClick = () => {
    if (onViewProduct) {
      onViewProduct(product?.slug || product?.sku || product?.id);
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(product);
    } else {
      addItemFn(product, 1);
    }
  };

  const staggerDelay = `${Math.min(Math.max(0, index) * 20, 200)}ms`;

  return (
    <Card
      data-card-variant="specs_heavy"
      style={{
        ...style,
        animationDelay: staggerDelay,
        '--stagger-delay': staggerDelay,
      }}
      className={cn(
        "group relative flex h-full flex-col overflow-hidden border border-[var(--border)] bg-white transition-all duration-200 hover:border-[var(--primary)] hover:shadow-md anim-card-stagger",
        className
      )}
      {...props}
    >
      <CardHeader className={cn('space-y-3 p-4', compact && 'p-3 space-y-2')}>
        
        {/* Top Tagline: Manufacturer & SKU */}
        <div className="flex items-center justify-between text-xs font-semibold">
          <span className="font-display font-extrabold uppercase tracking-wider text-[var(--primary,#e3000b)] truncate max-w-[150px]">
            {product?.manufacturer || 'Fachgroßhandel'}
          </span>
          <span className="font-mono text-xs text-[var(--secondary)] bg-[var(--surface-container-low,#f8fafc)] border border-[var(--border)] px-1.5 py-0.5 rounded">
            Art.-Nr. {sku}
          </span>
        </div>

        {/* Product Image Thumbnail */}
        <button
          type="button"
          onClick={handleCardClick}
          className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-xl border border-[var(--border)]/60 bg-[var(--surface-container-low,#f8fafc)] text-left focus:outline-none cursor-pointer"
          aria-label={`${product?.name} Produktdetails öffnen`}
        >
          {primaryImage ? (
            <img
              src={mediaUrl(primaryImage)}
              alt={product?.name || 'Produktabbildung'}
              loading="lazy"
              className="h-full w-full object-contain p-3 transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-[var(--secondary)]/50 gap-1">
              <Package className="h-8 w-8" />
              <span className="text-xs font-bold uppercase tracking-wider">Bild folgt</span>
            </div>
          )}

          {(product?.isFeatured || product?.is_featured) && (
            <span className="absolute top-2 left-2 rounded-md bg-[var(--primary)] px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-[var(--on-primary,white)] shadow-sm">
              Empfehlung
            </span>
          )}
        </button>

        {/* Product Title */}
        <button
          type="button"
          onClick={handleCardClick}
          className="text-left font-display text-sm sm:text-base font-bold leading-snug text-[var(--foreground)] hover:text-[var(--primary)] transition-colors line-clamp-2 min-h-[2.5rem] cursor-pointer"
        >
          {product?.name || 'Unbenanntes Produkt'}
        </button>

        {/* Specs Chips */}
        {specs && Object.keys(specs).length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {Object.entries(specs).slice(0, 3).map(([k, v]) => (
              <span
                key={k}
                className="inline-flex items-center gap-1 rounded bg-[var(--surface-container-low,#f8fafc)] border border-[var(--border)] px-1.5 py-0.5 text-xs font-mono text-[var(--secondary)]"
                title={`${k}: ${v}`}
              >
                <Cpu className="h-2.5 w-2.5 text-[var(--primary)] opacity-70" />
                <span>{String(v)}</span>
              </span>
            ))}
          </div>
        )}
      </CardHeader>

      {/* Footer: Price & CTA */}
      <CardContent className={cn('p-4 pt-0 mt-auto space-y-3', compact && 'p-3 pt-0')}>
        <div className="flex items-center justify-between border-t border-[var(--border)] pt-3">
          <div className="flex flex-col">
            <span className="text-xs font-bold uppercase tracking-widest text-[var(--secondary)]">
              {isBuyable ? 'Preis' : 'Status'}
            </span>
            <span className="font-display text-base sm:text-lg font-extrabold text-[var(--foreground)]">
              {isBuyable ? formattedPrice : 'Auf Anfrage'}
            </span>
          </div>

          {isBuyable ? (
            <Button
              size="sm"
              variant={alreadyInCart ? 'secondary' : 'default'}
              className="h-9 px-3 gap-1.5 rounded-lg shrink-0 font-bold transition-transform active:scale-95 cursor-pointer shadow-2xs"
              onClick={handleAddToCart}
              title={alreadyInCart ? (isStripeCheckout(tenant) ? 'Im Warenkorb' : 'Im Anfragekorb') : (isStripeCheckout(tenant) ? 'In den Warenkorb hinzufügen' : 'In den Anfragekorb')}
            >
              {alreadyInCart ? (
                <>
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span className="text-xs">Im Korb</span>
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4" />
                  <span className="text-xs">Anfragen</span>
                </>
              )}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-9 px-3 gap-1.5 rounded-lg shrink-0 font-medium cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (onInquiry) onInquiry(product);
                else handleCardClick();
              }}
              title="Projektangebot anfragen"
            >
              <Send className="h-3.5 w-3.5" />
              <span className="text-xs">Anfrage</span>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
